import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Collapse
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Event,
  Analytics,
  Speed,
  Group,
  Settings,
  Timeline,
  Phone,
  Email,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TracsessDetail: React.FC = () => {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCardClick = (cardId: string) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1, 
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/logo-square.png" 
              alt="Prodxpress Logo" 
              style={{ 
                height: '40px', 
                marginRight: '12px',
                maxWidth: '50px'
              }}
            />
            <img 
              src="/logo-title.png" 
              alt="PRODXPRESS" 
              style={{ 
                height: '24px', 
                maxWidth: '150px',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 10, md: 12 },
          pb: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #022179 0%, #1976D2 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ 
            color: 'white', 
            mb: 3, 
            fontWeight: 700,
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            fontFamily: 'Prompt, sans-serif'
          }}>
            Tracsess Ecosystem
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ 
            color: 'white', 
            opacity: 0.9, 
            mb: 2,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
          }}>
            Revolutionizing Motorsports Through Technology
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ 
            color: 'white', 
            opacity: 0.8, 
            mb: 4, 
            maxWidth: '800px', 
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            px: { xs: 2, sm: 0 }
          }}>
            A comprehensive platform connecting event organizers, professional tuners, and passionate racers 
            through three integrated applications designed specifically for the motorsports community.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://tracsess.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: 'white',
              color: '#022179',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '&:hover': {
                backgroundColor: '#f5f5f5',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Visit tracsess.com
          </Button>
        </Container>
      </Box>

      {/* Interactive Feature Cards */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ 
            mb: 6, 
            color: 'text.primary',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            Complete Feature Overview
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 3,
            maxWidth: '900px',
            mx: 'auto'
          }}>
            {/* Tracsess Organize Card */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(2,33,121,0.15)'
                },
                border: expandedCard === 'organize' ? '2px solid #468FEA' : '1px solid #e2e8f0'
              }}
              onClick={() => handleCardClick('organize')}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" gutterBottom sx={{ color: '#468FEA', mb: 2, fontWeight: 600, lineHeight: 1.3 }}>
                      Are you organizing track days, training sessions, and racing championships?
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <img 
                        src="/tracsess-organize-logo.svg" 
                        alt="Tracsess Organize Logo" 
                        style={{ 
                          height: '48px', 
                          width: '48px', 
                          marginRight: '12px'
                        }}
                      />
                      <Box>
                        <Typography variant="h5" sx={{ 
                          color: 'text.primary', 
                          fontWeight: 600,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          mb: 0.5
                        }}>
                          Tracsess Organize
                        </Typography>
                        <Chip label="Web & Mobile" size="small" sx={{ backgroundColor: '#468FEA', color: 'white' }} />
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      Then you know the challenges: managing registrations, coordinating with participants, handling payments, 
                      and ensuring everything runs smoothly on event day. Tracsess Organize eliminates these headaches with 
                      a complete platform designed specifically for motorsports event organizers like you.
                    </Typography>
                  </Box>
                  
                  <IconButton sx={{ ml: 2, mt: 1 }}>
                    {expandedCard === 'organize' ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandedCard === 'organize'}>
                  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
                      Complete Event Management Features
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Event sx={{ color: '#468FEA', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Event Creation & Management
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Create events with custom pricing & skill requirements</li>
                          <li>Set participant limits and vehicle restrictions</li>
                          <li>Manage multiple events simultaneously</li>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Group sx={{ color: '#468FEA', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Participant Management
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Real-time booking system with instant confirmations</li>
                          <li>Attendance tracking and management</li>
                          <li>Digital waiver management and signatures</li>
                          <li>Participant communication and notifications</li>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Analytics sx={{ color: '#468FEA', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Business Operations
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Financial reporting and payment tracking</li>
                          <li>Rental vehicles tracking and management</li>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Tracsess Tune Card */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(2,33,121,0.15)'
                },
                border: expandedCard === 'tune' ? '2px solid #03336b' : '1px solid #e2e8f0'
              }}
              onClick={() => handleCardClick('tune')}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" gutterBottom sx={{ color: '#03336b', mb: 2, fontWeight: 600, lineHeight: 1.3 }}>
                      Are you tuning and setting up race machines for racers?
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <img 
                        src="/tracsess-tune-logo.svg" 
                        alt="Tracsess Tune Logo" 
                        style={{ 
                          height: '48px', 
                          width: '48px', 
                          marginRight: '12px'
                        }}
                      />
                      <Box>
                        <Typography variant="h5" sx={{ 
                          color: 'text.primary', 
                          fontWeight: 600,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          mb: 0.5
                        }}>
                          Tracsess Tune
                        </Typography>
                        <Chip label="Mobile App" size="small" sx={{ backgroundColor: '#03336b', color: 'white' }} />
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      Then you understand the importance of documenting every modification, tracking performance gains, 
                      and proving your tuning expertise with real data. Tracsess Tune is built specifically for professional 
                      tuners and mechanics who want to showcase their work and deliver measurable results to their clients.
                    </Typography>
                  </Box>
                  
                  <IconButton sx={{ ml: 2, mt: 1 }}>
                    {expandedCard === 'tune' ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandedCard === 'tune'}>
                  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
                      Professional Tuning Platform Features
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Settings sx={{ color: '#03336b', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Modification Tracking
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Track your modifications and their effects on performance on an Event basis</li>
                          <li>Setup Weather conditions and track conditions</li>
                          <li>Setup vehicles and monitor lap times</li>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Speed sx={{ color: '#03336b', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Performance Analysis
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Lap time correlation with tuning changes</li>
                          <li>Record Riders/Drivers feedback for next setup plan</li>
                          <li>Fastest lap time for each session</li>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Group sx={{ color: '#03336b', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Client Management
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Direct communication with racers</li>
                          <li>Tuning history and recommendations</li>
                          <li>Performance reports for clients</li>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Tracsess (Racers) Card */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(2,33,121,0.15)'
                },
                border: expandedCard === 'racer' ? '2px solid #1357A6' : '1px solid #e2e8f0'
              }}
              onClick={() => handleCardClick('racer')}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" gutterBottom sx={{ color: '#1357A6', mb: 2, fontWeight: 600, lineHeight: 1.3 }}>
                      Want to get into motorsports, book track days, or become the next racing champion?
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <img 
                        src="/tracsess-logo.svg" 
                        alt="Tracsess Logo" 
                        style={{ 
                          height: '48px', 
                          width: '48px', 
                          marginRight: '12px'
                        }}
                      />
                      <Box>
                        <Typography variant="h5" sx={{ 
                          color: 'text.primary', 
                          fontWeight: 600,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          mb: 0.5
                        }}>
                          Tracsess
                        </Typography>
                        <Chip label="Mobile App" size="small" sx={{ backgroundColor: '#1357A6', color: 'white' }} />
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      Then Tracsess is your perfect companion! Whether you're taking your first steps on the track or 
                      chasing lap records, this app connects you with events, helps you track your progress, and puts you 
                      in touch with the right tuners and fellow racers to accelerate your motorsports journey.
                    </Typography>
                  </Box>
                  
                  <IconButton sx={{ ml: 2, mt: 1 }}>
                    {expandedCard === 'racer' ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandedCard === 'racer'}>
                  <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
                      Ultimate Racing Companion Features
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Event sx={{ color: '#1357A6', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Event Discovery & Booking
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Discover track days and events near you</li>
                          <li>Instant booking with secure payments</li>
                          <li>Digital check-in and event access</li>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Timeline sx={{ color: '#1357A6', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Performance Tracking
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Lap time tracking with your tuner for your setup</li>
                          <li>Personal best records</li>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 250px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Group sx={{ color: '#1357A6', mr: 1 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Community & Collaboration
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: '0.9rem' }}>
                          <li>Connect with tuners</li>
                          <li>Motorsports social networking</li>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Real-time Use Case
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ 
            mb: 4, 
            color: 'text.primary',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            Real-World Scenario
          </Typography>
          <Box sx={{ 
            p: { xs: 3, md: 4 }, 
            backgroundColor: 'white', 
            borderRadius: 4, 
            boxShadow: '0 4px 20px rgba(2,33,121,0.08)' 
          }}>
            <Typography variant="h5" gutterBottom sx={{ 
              color: 'primary.main', 
              mb: 3,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}>
              Meet Sarah, Mike, and Alex - The Tracsess Community
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 3, md: 4 }, mb: 4 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main' }}>
                  🏁 Sarah - Track Day Organizer
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                  Sarah runs weekend track events at local circuits. Using <strong>Tracsess Organize</strong>, she:
                </Typography>
                <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <li>Posts her upcoming track day event for March 15th</li>
                  <li>Sets participant limits, pricing, and skill level requirements</li>
                  <li>Manages real-time bookings from 50+ racers</li>
                  <li>Coordinates with local tuning shops for on-site support</li>
                  <li>Sends weather updates and track condition alerts</li>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main' }}>
                  🔧 Mike - Professional Tuner
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                  Mike owns a tuning shop and uses <strong>Tracsess Tune</strong> to:
                </Typography>
                <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <li>Track Alex's car modifications and tuning sessions</li>
                  <li>Record dyno results: 285hp → 320hp after his tune</li>
                  <li>Correlate lap time improvements with specific modifications</li>
                  <li>Maintain service history and recommend future upgrades</li>
                  <li>Share performance data directly with Alex</li>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main' }}>
                  🏎️ Alex - Racing Enthusiast
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                  Alex loves track days and uses <strong>Tracsess</strong> to:
                </Typography>
                <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <li>Book Sarah's track event with instant confirmation</li>
                  <li>Track his lap times: 1:45.2 → 1:42.8 after Mike's tune</li>
                  <li>Share progress with his racing buddy team</li>
                  <li>Access Mike's tuning recommendations</li>
                  <li>View performance analytics and improvement trends</li>
                </Box>
              </Box>
            </Box>
            
            <Typography variant="body1" sx={{ 
              color: 'text.primary', 
              fontStyle: 'italic', 
              textAlign: 'center',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}>
              "In one weekend, Sarah's event sells out, Mike optimizes 12 cars, and Alex achieves his personal best lap time. 
              All connected through the Tracsess ecosystem."
            </Typography>
          </Box>
        </Container>
      </Box> */}

      {/* Call to Action */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ 
            color: 'white', 
            mb: 3,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            Ready to Transform Your Track Experience?
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'white', 
            opacity: 0.9, 
            mb: 4,
            px: { xs: 2, sm: 0 }
          }}>
            Join the Tracsess community and connect with organizers, tuners, and racers worldwide
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              href="https://tracsess.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                backgroundColor: 'white', 
                color: 'primary.main', 
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              Visit tracsess.com
            </Button>
            <Button 
              variant="outlined" 
              size="large" 
              sx={{ 
                borderColor: 'white', 
                color: 'white', 
                '&:hover': { borderColor: '#f5f5f5', backgroundColor: 'rgba(255,255,255,0.1)' } 
              }}
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, backgroundColor: '#1E293B', color: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: '400px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 700, mb: 0 }}>
                  Prodxpress
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                Accelerating innovation through technology solutions that streamline operations, 
                connect communities, and drive progress across industries.
              </Typography>
            </Box>
            
            <Box sx={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Products
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/products/tracsess')}
                >
                  Tracsess
                </Button>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/products/nexpenx')}
                >
                  Nexpenx
                </Button>
                <Button sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}>
                  More Coming Soon
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/support/tracsess')}
                >
                  Account Help & Support
                </Button>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ flex: '1 1 250px', maxWidth: '350px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Contact Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    contact@prodxpress.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    +91 81222 96635
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    Chennai, India
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #475569', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
              © 2024 Prodxpress. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TracsessDetail; 